import React, { useCallback, useState } from "react";

/**
 * Global error context.
 *
 * Provides a central place to accumulate application-level errors from anywhere
 * in the component tree without prop-drilling an error handler through every
 * intermediate component. Currently used by the Configurator flow to surface
 * API failures; errors are appended (never cleared) so the full history is
 * available for display or debugging.
 *
 * @type {React.Context<{ errors: Error[], pushError: (error: Error) => void }>}
 */
const ErrorContext = React.createContext({
  errors: [],
  pushError: () => {},
});

/**
 * Provides the ErrorContext to its subtree.
 *
 * @param {React.PropsWithChildren} props
 */
export const ErrorContextProvider = (props) => {
  const [errors, setErrors] = useState([]);

  const pushError = useCallback((newError) => {
    setErrors((prev) => [...prev, newError]);
  }, []);

  const contextValue = {
    errors: errors,
    pushError: pushError,
  };

  return (
    <ErrorContext.Provider value={contextValue}>
      {props.children}
    </ErrorContext.Provider>
  );
};

export default ErrorContext;
